import { write } from '../data/db';
import { TABLE } from '../data/db';
import { session } from '../data/repos';
import { log } from './logger';

/**
 * §13.7 — the single-writer advisory lock, on the **Web Locks API**.
 *
 * ── What this is for, and what it deliberately is not ────────────────────────
 *
 * The installed PWA and a Safari tab of the same origin are two browsing contexts sharing one
 * IndexedDB on one device. §13.7's job is to notice that and **say so**. It is not to stop it:
 *
 * > **The lock reports. It never refuses.** A context that does not hold it stays **fully
 * > functional** and shows a persistent, non-blocking banner.
 *
 * ENV-10 is still respected — no merge, no conflict resolution, no coordination. This module
 * detects a second live context and reports it.
 *
 * ── Why refusing was wrong, verified rather than assumed ─────────────────────
 *
 * The earlier mechanism sent the second context read-only on the grounds that two writers would
 * silently corrupt `cachedCount`. They would not, and the code says so: `bus.runCommand` opens
 * **one** `readwrite` transaction per command and `decide()` reads its snapshot **inside** it, so
 * `handleIncrement` computes `prev+1` from a row read in the same transaction that writes it.
 * IndexedDB serializes overlapping-scope `readwrite` transactions across **every** connection to
 * the database, not merely within one, and ids are `crypto.randomUUID` (`app/ids.ts`) so two
 * contexts cannot collide on an Observation id. Two contexts each incrementing therefore produce
 * two Observations and two correct writes; INV-2 and INV-9 hold.
 *
 * What a second context actually costs is **confusion** — a contested `UiSession` screen pointer,
 * two mic streams, a user unsure which window is real. That is worth a banner and is not worth
 * refusing a tap. The first browser run of U-8 is the evidence for the other direction: every tap
 * dispatched, nothing committed, and *"Touchline is open in another window"* raised mid-half.
 *
 * ── Why the browser's lock and not a timestamp ───────────────────────────────
 *
 * The withdrawn mechanism minted a session id per page load and could not take a claim younger
 * than a 90-second stale window, so **a reload left the app degraded for up to 90 seconds while
 * blaming a competing window that did not exist** — and iOS purges and relaunches installed PWAs
 * routinely, most of all under the memory pressure of a ninety-minute match. A Web Lock has the
 * property the heartbeat was trying and failing to emulate: **the browser releases it when the
 * page is destroyed, and does not release it on backgrounding.**
 *
 * **The lock is deliberately NOT released on `pagehide`.** On iOS `pagehide` also fires on
 * backgrounding, so releasing there would drop the lock every time the user switches apps at
 * halftime — trading a 90-second window for a permanent one. Page destruction is the browser's
 * job and it is the entire point.
 *
 * ── No fallback path ─────────────────────────────────────────────────────────
 *
 * Web Locks ships in Safari from 15.4 against an ENV-1 target of 17+. If `navigator.locks` is
 * absent the app runs **unlocked**, which is the pre-existing default and no worse: this context
 * behaves as the holder and no banner ever appears. That is reported through OBS-1, not hidden.
 *
 * ── The open question, recorded rather than assumed ──────────────────────────
 *
 * Whether an installed home-screen PWA and a Safari tab of the same origin share a lock manager
 * the way they share IndexedDB is **not known** and is not resolvable on desktop; VER-3 item 9
 * settles it on the device. If they do not, the mechanism **fails open** — both contexts believe
 * they hold it — which is precisely the pre-existing default. No code here assumes either answer,
 * and nothing else in the design depends on it.
 */

const MODULE = 'writerLock';

/**
 * The lock name from design §13.7, module-private on purpose.
 *
 * NFR-9 puts thresholds and caps in `config/`; this is neither. It is a single-use identifier owned
 * by the one module that requests it, and `config/`'s §1.3 inventory is the design's to extend, not
 * this unit's. Exported only so a test can assert the name the device will actually contend on.
 */
export const WRITER_LOCK_NAME = 'touchline-writer';

/**
 * The state U-9 renders (SES-7's notification strip) and OBS-1 records.
 *
 * There is no `role` and no read-only mode any more: `holdsLock` gates a **banner**, never a
 * command. Any code that branches capture on this field has reintroduced the defect §13.7 was
 * re-decided to remove.
 */
export interface WriterStatus {
  /** Whether this context holds the Web Lock. `false` is the banner state — and nothing else. */
  readonly holdsLock: boolean;
  /** This context's id. Stable for the lifetime of the page. */
  readonly sessionId: string;
  /**
   * The **other** context's session id when this one does not hold the lock; `null` otherwise.
   *
   * Diagnostic, from the `ActiveMatch` row — the banner's detail line and OBS-1 want it, and
   * `navigator.locks.query()` does not carry it. Nothing branches on it.
   */
  readonly heldBy: string | null;
  /**
   * When the recorded holder last wrote (`heartbeatTs`), or `null` if nothing is recorded.
   *
   * **Nothing may branch on its age.** It is a "since when" for a human reading a banner or a debug
   * screen; the withdrawn mechanism's whole defect was code that compared it against a clock.
   */
  readonly heldSince: number | null;
}

/**
 * The one call §13.7 makes into the Web Locks API, as an interface rather than a global.
 *
 * This is a boundary, not a testing convenience. `fake-indexeddb` brings no Web Locks and the Node
 * suite has no `navigator` at all, so reaching for the global would make the mechanism untestable —
 * but more importantly, the **open device question** (do an installed PWA and a Safari tab share a
 * lock manager?) is a question about *which manager answers*, and it can only be probed, recorded
 * or substituted if the manager is a value this module is handed rather than one it reaches for.
 *
 * `navigator.locks` satisfies it structurally; so does a fake that grants once and then refuses.
 */
export interface WriterLockManager {
  request(
    name: string,
    options: { readonly mode: 'exclusive'; readonly ifAvailable: true },
    callback: (lock: unknown) => unknown,
  ): Promise<unknown>;
}

export interface WriterLockOptions {
  /**
   * The lock manager to contend on. Omitted takes `navigator.locks`; `null` runs unlocked, which is
   * what a runtime without Web Locks does and is therefore a real production path, not a stub.
   */
  readonly locks?: WriterLockManager | null;
}

/**
 * Identifies a browsing context, not a user and not a device.
 *
 * Regenerated on every page load, which under the withdrawn mechanism was half the defect and here
 * is simply true: a Web Lock belongs to a page, and a new page is a new contender.
 * `crypto.randomUUID` is available in every target runtime (iOS Safari 17+, and `node:crypto`'s
 * webcrypto in tests) and this is `app/`, so a host global is in bounds.
 */
let mySessionId: string = crypto.randomUUID();

/**
 * Optimistic before the first claim — no banner until something is actually known.
 *
 * Under the withdrawn mechanism this default was load-bearing and dangerous, because it decided
 * whether commands worked. It no longer decides anything a user can lose: the worst a wrong
 * optimistic default can now do is withhold a banner for the length of one `locks.request`.
 */
let cached: WriterStatus = {
  holdsLock: true,
  sessionId: mySessionId,
  heldBy: null,
  heldSince: null,
};

/**
 * Whether `claimWriter` has ever run in this context.
 *
 * If a future bootstrap forgets to claim, §13.7 is silently **absent** — no lock is ever requested,
 * no second context is ever detected, and no banner can ever appear, with nothing anywhere saying
 * so. That is a claimed invariant with no mechanism, which is the exact failure this module exists
 * to fix, so the omission gets its own alarm rather than a comment asking the next unit to
 * remember: {@link assertClaimed} raises it once, at `error`.
 */
let claimed = false;

/**
 * Whether the browser has granted this page the lock.
 *
 * Tracked separately from {@link cached} because a grant and the record of it are two steps, and the
 * second can fail. Without this, a storage fault between them would leave the module believing it
 * does not hold a lock the browser has already given it — and every later re-attempt would then ask
 * for a lock **this same page** is holding exclusively, be refused, and report a second window that
 * does not exist. That is the withdrawn mechanism's defect, rebuilt out of different parts.
 *
 * Never cleared while the page lives: only page destruction releases a Web Lock, and this module
 * deliberately has no release path (see the note on `pagehide` above).
 */
let lockGranted = false;

export function status(): WriterStatus {
  return cached;
}

/**
 * Subscribers to the banner's condition (build unit U-9a, §5.6's notification strip).
 *
 * §13.7's banner has to *clear itself* when the re-attempt succeeds: *"closing the other context and
 * returning to this one clears the banner with no user action beyond the return."* A surface that
 * merely read {@link status} at render time would keep showing the banner until something else
 * happened to re-render it, and — worse — would race the async re-attempt the same
 * `visibilitychange` kicks off.
 *
 * A subscription is the alternative to both, and it introduces no cadence: it fires only where
 * `cached` is assigned, which is exactly the two places a claim is decided.
 */
const statusListeners = new Set<(status: WriterStatus) => void>();

export function subscribeWriterStatus(listener: (status: WriterStatus) => void): () => void {
  statusListeners.add(listener);
  return () => {
    statusListeners.delete(listener);
  };
}

/** The single assignment point for {@link cached}, so no path can change it without reporting. */
function publish(next: WriterStatus): WriterStatus {
  cached = next;
  for (const listener of statusListeners) listener(next);
  return next;
}

/** True once this context has actually run the §13.7 claim. */
export function hasClaimed(): boolean {
  return claimed;
}

export function sessionId(): string {
  return mySessionId;
}

/**
 * Reports an unclaimed context, once.
 *
 * Called by the bus before its first command. Deliberately a **log line, not a refusal** — and now
 * doubly so, because §13.7 no longer refuses anything at all. What is lost when nobody claims is
 * the banner, so the alarm makes the omission findable on the debug screen (OBS-2) rather than
 * invisible until two windows confuse someone at a venue.
 */
export function assertClaimed(): void {
  if (claimed) return;
  claimed = true; // once only — this must not fire on every tap of a half
  log.error(
    MODULE,
    '§13.7: a command was dispatched before claimWriter() ran, so no Web Lock was ever requested. A second window on this device would never be detected and no banner could appear.',
    { sessionId: mySessionId },
  );
}

/**
 * Requests the lock, without waiting.
 *
 * `ifAvailable: true` makes the answer immediate — granted or `null`, never a wait — which is why
 * this can sit on the cold-launch path. The granted branch returns a promise that **never settles**,
 * so the browser holds the lock until it destroys the page; that also means the `request()` promise
 * never settles either, and the grant has to be reported out of the callback rather than awaited.
 *
 * @returns `true` if this context now holds the lock, `false` if another live context does.
 */
async function requestLock(locks: WriterLockManager): Promise<boolean> {
  return new Promise<boolean>((resolve, reject) => {
    let settled = false;
    const answer = (held: boolean): void => {
      if (settled) return;
      settled = true;
      resolve(held);
    };

    const granted = locks.request(
      WRITER_LOCK_NAME,
      { mode: 'exclusive', ifAvailable: true },
      (lock: unknown) => {
        if (lock === null || lock === undefined) {
          answer(false);
          return undefined; // releases immediately; nothing was granted
        }
        answer(true);
        // Held until this page is destroyed. NOT released on `pagehide` — see the module note.
        return new Promise<never>(() => {});
      },
    );

    // Only reachable on a failed request (the granted branch never settles and the refused branch
    // has already answered). A throwing lock manager must not become an unhandled rejection.
    granted.catch((cause: unknown) => {
      if (settled) return;
      settled = true;
      reject(cause instanceof Error ? cause : new Error(String(cause)));
    });
  });
}

/** `navigator.locks`, or `null` on a runtime without it (Node under test; a browser below 15.4). */
function hostLockManager(): WriterLockManager | null {
  if (typeof navigator === 'undefined') return null;
  const locks: LockManager | undefined = navigator.locks;
  return locks ?? null;
}

/**
 * Acquires the lock and records the diagnostic row, or discovers that another live context holds it.
 *
 * @param now wall clock, injected — it is written into the record, never compared against it.
 * @throws {StorageError} if the singleton cannot be read or written.
 */
export async function claimWriter(now: number, options: WriterLockOptions = {}): Promise<WriterStatus> {
  const locks = options.locks === undefined ? hostLockManager() : options.locks;
  // Set before the await, not after. `claimed` answers "did anything ever run the claim", which is
  // the omission {@link assertClaimed} guards; a claim that ran and *failed* is already reported by
  // its own throw and by `bootstrap`'s handler, and must not also raise the never-wired alarm.
  claimed = true;
  publish(await acquireAndRecord(now, locks));

  if (cached.holdsLock) {
    log.info(MODULE, 'writer lock held', { sessionId: mySessionId });
  } else {
    log.warn(MODULE, 'another window holds the writer lock; this context reports but is NOT restricted', {
      heldBy: cached.heldBy,
      heldSince: cached.heldSince,
      sessionId: mySessionId,
    });
  }
  return cached;
}

/**
 * Re-attempts a lock this context does not hold. A no-op for the holder.
 *
 * §13.7's banner is *"truthful by construction"* only because a Web Lock is held by a live page, so
 * failing to acquire means another context exists **right now**. "Right now" is true at the instant
 * of the request and decays afterwards: once the other window closes, a context that never asks
 * again would keep blaming a window that no longer exists — which is the shape of the defect the
 * re-decision removed. `bootstrap` calls this on `visibilitychange`, the moment a returning user is
 * most likely to have just closed the other one.
 *
 * @throws {StorageError} if the singleton cannot be read or written.
 */
export async function refreshWriterLock(
  now: number,
  options: WriterLockOptions = {},
): Promise<WriterStatus> {
  if (cached.holdsLock) return cached;

  const locks = options.locks === undefined ? hostLockManager() : options.locks;
  const next = await acquireAndRecord(now, locks);
  const gained = next.holdsLock;
  publish(next);

  if (gained) {
    log.info(MODULE, 'the other window is gone; this context now holds the writer lock', {
      sessionId: mySessionId,
    });
  }
  return cached;
}

/**
 * The shared body of {@link claimWriter} and {@link refreshWriterLock}.
 *
 * The holder writes the diagnostic record; a non-holder only **reads** it. A non-holder that wrote
 * would overwrite the real holder's `writerSessionId` with its own and the banner's detail line
 * would then name the window the user is looking at.
 */
async function acquireAndRecord(
  now: number,
  locks: WriterLockManager | null,
): Promise<WriterStatus> {
  if (!(await holdsLock(locks))) {
    const row = await session.readActiveMatch();
    return {
      holdsLock: false,
      sessionId: mySessionId,
      heldBy: row?.writerSessionId ?? null,
      heldSince: row?.heartbeatTs ?? null,
    };
  }

  await write(
    [TABLE.activeMatch],
    async () => {
      const row = await session.readActiveMatch();
      await session.writeActiveMatch({
        // The previous context's resume pointer is preserved: it died or was closed mid-match, and
        // the match it was capturing is exactly what SES-3 must resume into.
        matchId: row?.matchId ?? null,
        writerSessionId: mySessionId,
        heartbeatTs: now,
      });
    },
    { label: 'claimWriter' },
  );

  return { holdsLock: true, sessionId: mySessionId, heldBy: null, heldSince: now };
}

/** `true` when this context holds the lock, or when there is no lock manager to hold one. */
async function holdsLock(locks: WriterLockManager | null): Promise<boolean> {
  // Asking again for a lock this page already holds exclusively would be refused by the browser.
  if (lockGranted) return true;

  if (locks === null) {
    log.warn(
      MODULE,
      '§13.7: the Web Locks API is unavailable, so this context runs unlocked. A second window would not be detected.',
      { sessionId: mySessionId },
    );
    lockGranted = true;
    return true;
  }

  try {
    const granted = await requestLock(locks);
    if (granted) lockGranted = true;
    return granted;
  } catch (cause) {
    // Failing open is the specified degradation: unlocked is the pre-existing default and no worse
    // than a runtime without the API. Refusing to run would be strictly worse than the problem.
    log.exception(MODULE, '§13.7: the writer lock could not be requested; running unlocked', cause);
    lockGranted = true;
    return true;
  }
}

/**
 * The in-transaction diagnostic write, called by the bus's applier.
 *
 * Every state transition passes through here, so the record travels in the same commit that moves
 * the match — no separate write and no half-updated pointer. It carries §2.5's resume pointer,
 * which is why **every** context writes it, holder or not: a fully-functional second context that
 * started a match and did not record it would break SES-3's resume.
 *
 * The record is therefore "the most recent writer, and when" — which for the single-context case
 * the product is actually built around is exactly "who holds the lock, and since when". Nothing
 * branches on either field.
 *
 * @param matchId `undefined` leaves the resume pointer alone (§2.2's `activeMatch: 'unchanged'`);
 *   a string sets it; `null` clears it.
 * @throws {StorageError} outside a transaction.
 */
export async function touchInTransaction(
  now: number,
  matchId: string | null | undefined,
): Promise<void> {
  const row = await session.readActiveMatch();
  await session.writeActiveMatch({
    matchId: matchId === undefined ? (row?.matchId ?? null) : matchId,
    writerSessionId: mySessionId,
    heartbeatTs: now,
  });
}

/** Test seam. Resets the cached state and mints a fresh context identity. */
export function __resetWriterLockForTests(nextSessionId?: string): void {
  mySessionId = nextSessionId ?? crypto.randomUUID();
  cached = { holdsLock: true, sessionId: mySessionId, heldBy: null, heldSince: null };
  claimed = false;
  lockGranted = false;
  statusListeners.clear();
}
